import type { Actor } from '../types/actor.js';
import type { ApprovalRequest } from '../types/approval.js';
import type { LevelMap } from '../types/rbac.js';
import type { Tenant } from '../types/tenant.js';
import type { StaffAccess, TenantUser } from '../types/user.js';
import { personaOf } from './permissions.js';

/** At most two people hold the superadmin role: one accountable, one backup. */
export const MAX_SUPERADMINS = 2;

export function superadmins(users: readonly TenantUser[]): TenantUser[] {
  return users.filter((u) => u.isSuperadmin);
}

export type SuperadminChange = { ok: true } | { ok: false; message: string };

/** A backup can be appointed while there is room and the person is active. */
export function canAppointSuperadmin(users: readonly TenantUser[], candidate: TenantUser): SuperadminChange {
  if (candidate.isSuperadmin) return { ok: false, message: `${candidate.name} is already a superadmin.` };
  if (candidate.status !== 'Active') return { ok: false, message: 'The new superadmin must have signed in at least once.' };
  if (superadmins(users).length >= MAX_SUPERADMINS) {
    return { ok: false, message: `There are already ${MAX_SUPERADMINS} superadmins. Remove one first.` };
  }
  return { ok: true };
}

/** A superadmin can be removed only while another one remains. */
export function canRemoveSuperadmin(users: readonly TenantUser[], target: TenantUser): SuperadminChange {
  if (!target.isSuperadmin) return { ok: false, message: `${target.name} is not a superadmin.` };
  if (superadmins(users).length <= 1) return { ok: false, message: 'An organization always keeps at least one superadmin. Appoint another first.' };
  return { ok: true };
}

/**
 * People who would newly hold full management after a change. Any such change is
 * sensitive and goes through approval.
 */
export function managementGainers(before: Record<string, LevelMap>, after: Record<string, LevelMap>): string[] {
  return Object.keys(after).filter((id) => after[id]?.management === 'full' && before[id]?.management !== 'full');
}

/**
 * Who may decide a request. Never the requester. Tenant scope: another superadmin of that
 * tenant, or any DinoCloud staff member. DinoCloud scope: another Platform Owner.
 */
export function canDecideApproval(actor: Actor, request: ApprovalRequest, tenantUsers: readonly TenantUser[]): boolean {
  if (request.status !== 'pending') return false;
  const persona = personaOf(actor);
  const actingEmail = actor.kind === 'staff' ? actor.email : persona.email;
  if (actingEmail === request.requestedBy) return false;

  if (request.scope === 'dinocloud') {
    return actor.kind === 'staff' && !actor.impersonating && actor.role === 'superadmin';
  }
  if (actor.kind === 'staff') return true;
  return actor.tenant.tenantId === request.tenantId && tenantUsers.some((u) => u.id === actor.userId && u.isSuperadmin && u.status === 'Active');
}

/** Eligible approvers other than the requester. Empty means the change applies directly. */
export function eligibleApprovers(
  request: Pick<ApprovalRequest, 'scope' | 'requestedBy'>,
  tenantUsers: readonly TenantUser[],
  staff: readonly StaffAccess[],
): string[] {
  const activeStaff = staff.filter((s) => s.status === 'Active');
  if (request.scope === 'dinocloud') {
    return activeStaff.filter((s) => s.role === 'superadmin' && s.email !== request.requestedBy).map((s) => s.email);
  }
  const owners = tenantUsers.filter((u) => u.isSuperadmin && u.status === 'Active' && u.email !== request.requestedBy).map((u) => u.email);
  return [...owners, ...activeStaff.filter((s) => s.email !== request.requestedBy).map((s) => s.email)];
}

export function isApprovalExpired(request: ApprovalRequest, now = Date.now()): boolean {
  return new Date(request.expiresAt).getTime() < now;
}

/** Access reviews are due every 90 days, counted from the last one or from tenant creation. */
export const ACCESS_REVIEW_PERIOD_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface AccessReviewStatus {
  dueAt: string;
  overdue: boolean;
  /** Due within the next 14 days. */
  dueSoon: boolean;
  lastCompletedAt: string | null;
  completedBy: string | null;
}

export function accessReviewStatus(tenant: Pick<Tenant, 'createdAt' | 'accessReview'>, now = Date.now()): AccessReviewStatus {
  const from = tenant.accessReview?.lastCompletedAt ?? tenant.createdAt;
  const dueAt = new Date(new Date(from).getTime() + ACCESS_REVIEW_PERIOD_DAYS * DAY_MS);
  return {
    dueAt: dueAt.toISOString(),
    overdue: dueAt.getTime() < now,
    dueSoon: dueAt.getTime() - now < 14 * DAY_MS,
    lastCompletedAt: tenant.accessReview?.lastCompletedAt ?? null,
    completedBy: tenant.accessReview?.completedBy ?? null,
  };
}

/** Sign-in method for an email, from its tenant's SSO configuration. */
export function ssoFor(tenant: Pick<Tenant, 'ssoProvider' | 'status'> | null): { providerName: string; label: string; enforce: boolean } | null {
  if (!tenant?.ssoProvider || tenant.status === 'Suspended') return null;
  const { providerName, label, enforce } = tenant.ssoProvider;
  return { providerName, label, enforce };
}
